import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUser, fetchWithAuth, isAuthenticated, setUser } from "../../auth/auth";
import LoadingSpinner from "../../components/loadingSpinner/loadingSpinner";
import { useToast } from "../../components/toast/toast";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import { getApiUrl } from "../../utils/getApiUrl";
import type { UserModel, UserParamsMap } from "../../type/user";
import ProfilePanel from "./components/profilePanel";
import ParametersPanel from "./components/parametersPanel";
import "./userSettings.scss";

function serializeParameters(params: UserParamsMap): string {
  const arr = Object.entries(params).map(([key, entry]) => ({
    paramname: key,
    value: entry.value,
    group: entry.group,
    label: entry.label,
    description: entry.description,
    paramtype: entry.paramtype,
  }));
  return JSON.stringify(arr);
}

export default function UserSettings() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [user, setLocalUser] = useState<UserModel | null>(null);
  const [username, setUsername] = useState("");
  const [avatar, setAvatar] = useState("");
  const [parameters, setParameters] = useState<UserParamsMap>({});

  const initialized = useRef(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }
    fetchUser().then((u) => {
      if (!u) return;
      setLocalUser(u);
      setUsername(u.username ?? "");
      setAvatar(u.avatar ?? "");
      setParameters(u.parameters ?? {});
      initialized.current = true;
    });
  }, [navigate]);

  // Auto-save with debounce whenever fields change after initial load
  useEffect(() => {
    if (!initialized.current || !user) return;

    const timer = setTimeout(async () => {
      const payload = {
        iduser: user.iduser,
        username,
        avatar,
        admin: user.admin,
        parameters: serializeParameters(parameters),
      };
      
      try {
        const res = await fetchWithAuth(getApiUrl("user/update"), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const updated = await fetchUser();
        if (updated) {
          setUser(updated);
          setLocalUser(updated);
          window.dispatchEvent(new Event("auth-change"));
        }
      } catch (err: unknown) {
        showToast(err instanceof Error ? err.message : t("Zapisywanie nie powiodło się."), "error");
      }
    }, 600);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, avatar, parameters]);

  const handleParamChange = (key: string, value: boolean | string | number) => {
    setParameters((prev) => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  if (!user) {
    return (
      <div className="app-content__mainpanel">
        <LoadingSpinner className="user-settings__loading" label={t("Ładowanie ustawień…")}/>
      </div>
    );
  }

  return (
    <div className="app-content__mainpanel">
      <section className="row">
        <div className="app-content__panelleft panelleft-profile-panel">
          <ProfilePanel
            user={user}
            username={username}
            avatar={avatar}
            onUsernameChange={setUsername}
            onAvatarChange={setAvatar}
          />
        </div>

        <div className="app-content__panelright">
          <ParametersPanel
            parameters={parameters}
            onParamChange={handleParamChange}
            isAdmin={user.admin}
          />
        </div>
      </section>


    </div>
  );
}
